import { useQuery } from '@tanstack/react-query';
import { fetchTutorSystemCapabilities } from '../api/tutorCapabilities.client';
import {
  fetchRuntimeQuota,
  type RuntimeQuotaResponse,
} from '../api/quotaRuntime.client';
import { tutorRuntimeQuotaQueryKey } from '../queryKeys';
import { runtimeQuotaApiFeatureName } from '../utils/runtimeQuotaFeatureIdentity';
import { isTutorQueryIdentityReady } from '../utils/tutorQueryIdentity';
import { shouldRetryRuntimeQuotaQuery } from './runtimeQuotaQueryPolicy';
import { awaitRuntimeEndFinalizationIfPresent } from '../session/runtimeEndFinalizationStore';
import { useTutorQueryIdentity } from './useTutorQueryIdentity';

export const TUTOR_RUNTIME_QUOTA_STALE_MS = 20_000;

export type UseTutorRuntimeQuotaOptions = {
  featureName: string;
  enabled?: boolean;
};

async function fetchRuntimeQuotaWhenV2(featureName: string): Promise<RuntimeQuotaResponse | null> {
  const caps = await fetchTutorSystemCapabilities().catch(() => null);
  if (!caps?.quota_runtime_v2_enabled) {
    return null;
  }
  return fetchRuntimeQuota(runtimeQuotaApiFeatureName(featureName));
}

export { fetchRuntimeQuotaWhenV2, shouldRetryRuntimeQuotaQuery };

export function useTutorRuntimeQuota(opts: UseTutorRuntimeQuotaOptions) {
  const { membershipKey, studentKey } = useTutorQueryIdentity();
  const apiFeature = runtimeQuotaApiFeatureName(opts.featureName);
  const identityReady = isTutorQueryIdentityReady(membershipKey, studentKey);
  const featureReady = Boolean(apiFeature);
  const enabled = (opts.enabled ?? true) && identityReady && featureReady;

  const queryKey = tutorRuntimeQuotaQueryKey({
    membershipKey,
    studentKey,
    featureName: apiFeature,
  });

  const query = useQuery<RuntimeQuotaResponse | null, Error>({
    queryKey,
    queryFn: async () => {
      if (identityReady) {
        await awaitRuntimeEndFinalizationIfPresent(membershipKey, studentKey);
      }
      return fetchRuntimeQuotaWhenV2(apiFeature);
    },
    enabled,
    staleTime: TUTOR_RUNTIME_QUOTA_STALE_MS,
    retry: shouldRetryRuntimeQuotaQuery,
  });

  return {
    ...query,
    queryKey,
    runtimeQuota: query.data ?? null,
    runtimeV2Enabled: query.data != null,
    apiFeatureName: apiFeature,
    identityReady,
  };
}
